import { META_PREFIX } from '@de/utils/binding-enhancer/constants';
import { COMPONENT_TRACKING_DEFAULTS } from '@de/components/Trackable/componentTrackingDefaults';

// Flat dotted tracking prop keys on the raw `data.content` tree (before the
// binding resolver strips the `_meta.` prefix).
const NODE_TRACKING_PROP = `${META_PREFIX}tracking`; // '_meta.tracking'
const NODE_TRACKING_KIND_PROP = `${NODE_TRACKING_PROP}.kind`;

type ComponentNode = {
  readonly type?: unknown;
  readonly props?: Readonly<Record<string, unknown>>;
};

const isComponentNode = (value: unknown): value is ComponentNode =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  typeof (value as ComponentNode).type === 'string';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

// Effective tracking `kind`: per-instance `_meta.tracking.kind` wins over the
// flow registry (mirrors `createBoundRender.mergeTrackingMeta`). A host
// `componentTrackingDefaults` map is not consulted here; entry-point product
// detection stays on the flow registry.
// `_meta.tracking.enabled: false` does not change kind — a disabled Product
// placement still declares the Product surface so the entry unit does not
// emit a fallback Product lifecycle. An authored `kind: 'element'` override
// remains a different event family, not Product-off.
function resolveNodeKind(node: ComponentNode): string | undefined {
  const props = node.props ?? {};

  // Accept both the flat-object (`'_meta.tracking': { kind }`) and fully-flat
  // (`'_meta.tracking.kind': 'product'`) author forms.
  const trackingSlice = props[NODE_TRACKING_PROP];
  if (isPlainObject(trackingSlice) && typeof trackingSlice.kind === 'string') {
    return trackingSlice.kind;
  }
  const flatKind = props[NODE_TRACKING_KIND_PROP];
  if (typeof flatKind === 'string') return flatKind;

  const type = node.type;
  if (typeof type === 'string') {
    return COMPONENT_TRACKING_DEFAULTS[type]?.kind;
  }
  return undefined;
}

// True when any node in the config tree resolves to `kind: 'product'`,
// including placements with `_meta.tracking.enabled: false`.
// `useEntryPointEventFiring` uses this to suppress the entry unit's Product
// when content already claims that surface. Reads the config tree statically
// so it's correct before async data resolves (a runtime mount check would race
// the fetch and fire a spurious product on first paint).
export function contentDeclaresProductSurface(content: unknown): boolean {
  return walk(content);
}

function walk(node: unknown): boolean {
  if (Array.isArray(node)) {
    for (const item of node) {
      if (walk(item)) return true;
    }
    return false;
  }
  if (!isComponentNode(node)) return false;

  if (resolveNodeKind(node) === 'product') return true;

  const props = node.props ?? {};
  for (const value of Object.values(props)) {
    if (Array.isArray(value) || isComponentNode(value)) {
      if (walk(value)) return true;
    }
  }
  return false;
}
